import React from "react"
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverCloseButton,
  PopoverHeader,
  PopoverBody,
  Text,
  Divider,
  Button,
  Flex,
  useDisclosure,
  Image,
  useToast,
} from "@chakra-ui/react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import CartButton from "./CartButton"
import { COLORS } from "../../../constants/constants"
import flightImages from "../../../flightImages"
import QFLogo from "../../../img/QF.svg"
import VALogo from "../../../img/VA.svg"
import AALogo from "../../../img/AA.svg"

import deleteIcon from "../../../img/trash.svg"
import {
  removeFlightFromCart,
  removeAllFlightsFromCart,
} from "../../../services/api"
import { formatCartDate } from "../../../helpers/functions"
import {
  handleTrackAirlineButton,
  handleTrackCartButton,
} from "../../../services/analytics"

const flightSourceLogos = { QF: QFLogo, VA: VALogo, AA: AALogo }
const flightSourceLogosAlt = { QF: "qantas", VA: "velocity", AA: "american" }
const airlines = {
  QF: "Qantas Airlines",
  VA: "Velocity Airlines",
  AA: "American Airlines",
}

const CartMenu = ({ userCarts }) => {
  const { t } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()
  const { isOpen, onOpen, onClose } = useDisclosure()

  const { mutateAsync: removeFlightMutation } = useMutation({
    mutationFn: removeFlightFromCart,
    onSuccess: () => {
      toast({
        title: t("cart.removeSuccess") || "Flight removed from cart",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "bottom-right",
      })

      queryClient.invalidateQueries(["userCarts"])
    },
    onError: (error) => {
      toast({
        title: t("cart.removeError") || "Failed to remove flight from cart",
        description: error.response?.data?.detail || error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom-right",
      })
    },
  })

  const { mutateAsync: removeAllFlightsFromCartMutation } = useMutation({
    mutationFn: removeAllFlightsFromCart,
    onSuccess: () => {
      toast({
        title: t("cart.clearSuccess") || "Cart cleared successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "bottom-right",
      })
      queryClient.invalidateQueries(["userCarts"])
    },
    onError: (error) => {
      toast({
        title: t("cart.clearError") || "Failed to clear cart",
        description: error.response?.data?.detail || error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom-right",
      })
    },
  })

  if (!userCarts) return null

  const totalPoints = userCarts.reduce((acc, item) => {
    return acc + item.points_required
  }, 0)

  const taxesArray = userCarts.reduce((acc, item) => {
    const { currency, amount } = item.taxes

    const existingTax = acc.find((tax) => tax.currency === currency)

    if (existingTax) {
      existingTax.amount += amount
    } else {
      acc.push({ currency, amount })
    }
    return acc
  }, [])

  const totalTaxes = taxesArray.map((tax) => {
    return `${tax.currency}${Math.floor(tax.amount)}`
  })

  const handleRemoveFlight = async (id) => {
    await removeFlightMutation(id)
  }

  const handleClearCart = async () => {
    const ids = userCarts.map((item) => item.id.toString())
    await removeAllFlightsFromCartMutation(ids)
  }

  const handleLinkClick = (airline) => {
    handleTrackAirlineButton({ name: airline })
  }

  const handleCartButtonClick = () => {
    handleTrackCartButton({
      action: "clicked",
      cart_items_count: userCarts.length,
    })
    isOpen ? onClose() : onOpen()
  }

  return (
    <Popover
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      placement="bottom-end"
      closeOnBlur={true}
    >
      <PopoverTrigger>
        <Flex>
          <CartButton
            count={userCarts.length}
            onClick={handleCartButtonClick}
          />
        </Flex>
      </PopoverTrigger>

      <PopoverContent
        borderRadius="16px"
        w="520px"
        p={6}
        maxH="80vh"
        overflowY="auto"
        outline="none"
        _focus={{ boxShadow: "none" }}
        rowGap={6}
      >
        <PopoverArrow />
        <PopoverCloseButton
          top={"26px"}
          right={6}
          sx={{
            "& > svg": {
              width: "14px",
              height: "14px",
              color: COLORS.secondary,
            },
          }}
        />
        <PopoverHeader
          textAlign={"left"}
          fontWeight={700}
          fontSize={24}
          p={0}
          color={COLORS.black300}
          borderBottom={"none"}
          lineHeight={"28px"}
        >
          Itinerary
        </PopoverHeader>
        <PopoverBody p={0}>
          {userCarts.length > 0 ? (
            <Flex flexDirection={"column"} gap={5}>
              <Flex flexDirection={"column"} gap={4} width={"100%"}>
                {userCarts.map((item) => {
                  const planeImage =
                    item.length >= 3
                      ? flightImages("group_3_plus")
                      : flightImages(item.aircraft_type[0].slice(0, 2))

                  const points = item.points_required

                  const sourceLogo = flightSourceLogos[item.source]

                  const airline = airlines[item.source]

                  return (
                    <Flex
                      key={item.id}
                      flexDirection={"column"}
                      gap={4}
                      width={"100%"}
                    >
                      <Flex justifyContent={"space-between"}>
                        <Flex columnGap={3}>
                          <Flex
                            border={"1px solid #E8EBED"}
                            borderRadius={12}
                            padding={"10px 6px"}
                            width={"fit-content"}
                            boxShadow="0px 2px 8px rgba(20, 23, 37, 0.08)"
                          >
                            <Image
                              src={planeImage}
                              width={"40px"}
                              alt={item.aircraft_type}
                            />
                          </Flex>

                          <Flex flexDirection={"column"} gap={"2px"}>
                            <Flex>
                              <Text
                                color={COLORS.black300}
                                fontSize={"14px"}
                                fontWeight={700}
                                lineHeight={"20px"}
                              >
                                {formatCartDate(item.departure_date)}
                              </Text>
                            </Flex>
                            <Flex>
                              <Text
                                color={COLORS.black300}
                                fontSize={"12px"}
                                fontWeight={600}
                                lineHeight={"16px"}
                              >
                                {item.origin_code} - {item.destination_code}
                              </Text>
                            </Flex>
                            <Flex>
                              <Text
                                color={COLORS.black300}
                                fontSize={"12px"}
                                fontWeight={600}
                                lineHeight={"16px"}
                              >
                                {item.aircraft_type[0].split("(")[0] || ""}
                                {item.aircraft_type.length > 1 && " | "}
                                {item.aircraft_type[1] || ""}
                              </Text>
                            </Flex>
                          </Flex>
                        </Flex>
                        <Flex
                          columnGap={5}
                          height={"fit-content"}
                          alignItems={"center"}
                        >
                          <Flex
                            flexDirection={"column"}
                            gap={1}
                            textAlign={"right"}
                          >
                            <Text
                              color={COLORS.red}
                              fontSize={"14px"}
                              fontWeight={600}
                              lineHeight={"20px"}
                            >
                              {points} pts{" "}
                              <Text
                                as="span"
                                fontSize={"12px"}
                                lineHeight={"18px"}
                                fontWeight={600}
                              >
                                +{item.taxes.currency}
                                {Math.floor(item.taxes.amount)}
                              </Text>
                            </Text>
                            <Text
                              color={COLORS.black300}
                              fontSize={"12px"}
                              lineHeight={"16px"}
                              fontWeight={600}
                            >
                              {item.selected_cabin_class} Class
                            </Text>{" "}
                          </Flex>
                          <Flex gap={1} alignItems={"center"}>
                            <Flex
                              border={"1px solid #E8EBED"}
                              borderRadius={12}
                              padding={"6px"}
                              width={"fit-content"}
                              height={"fit-content"}
                              boxShadow="0px 2px 5px rgba(0, 0, 0, 0.1)"
                              cursor={"pointer"}
                              onClick={() => handleRemoveFlight(item.id)}
                            >
                              <Image
                                src={deleteIcon}
                                width={5}
                                height={5}
                                alt="Remove icon"
                              />
                            </Flex>

                            <Flex>
                              <Button
                                as="a"
                                href={item.deep_url}
                                onClick={() => handleLinkClick(airline)}
                                target="_blank"
                                backgroundColor={"transparent"}
                                width={"fit-content"}
                                height={"fit-content"}
                                minWidth={"unset"}
                                sx={{
                                  padding: 0,
                                  _hover: { backgroundColor: "transparent" },
                                }}
                              >
                                <Image
                                  src={sourceLogo}
                                  width={"30px"}
                                  height={"30px"}
                                  alt={t(
                                    `australianFlights.howToBookImageAlt`,
                                    {
                                      program:
                                        flightSourceLogosAlt[item.source],
                                      to: item.destination_city,
                                    }
                                  )}
                                />
                              </Button>
                            </Flex>
                          </Flex>
                        </Flex>
                      </Flex>

                      <Divider borderColor={COLORS.grey400} />
                    </Flex>
                  )
                })}
              </Flex>

              <Flex justifyContent={"flex-end"}>
                <Text
                  color={COLORS.red}
                  fontSize={"18px"}
                  fontWeight={600}
                  lineHeight={"24px"}
                >
                  {totalPoints} pts{" "}
                  {totalTaxes.map((tax) => (
                    <Text
                      key={tax}
                      as="span"
                      fontSize={"16px"}
                      lineHeight={"21px"}
                      fontWeight={600}
                    >
                      {" "}
                      +{tax}
                    </Text>
                  ))}
                </Text>
              </Flex>
            </Flex>
          ) : (
            <Flex alignItems={"center"}>
              <Text>Add a flight to your itinerary</Text>
            </Flex>
          )}

          <Flex justifyContent={"space-between"} mt={2}>
            <Button
              bg={COLORS.grey}
              color={COLORS.red}
              w="full"
              maxW={"154px"}
              borderRadius={"8px"}
              onClick={handleClearCart}
              isDisabled={userCarts.length === 0}
            >
              <Text
                fontSize={"14px"}
                fontWeight={700}
                lineHeight={"18px"}
                textTransform={"uppercase"}
              >
                Clear Itinerary
              </Text>
            </Button>
          </Flex>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  )
}

export default CartMenu
